import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const isHomePage = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);
  // Trap focus in mobile menu when open
  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      const focusableElements = mobileMenuRef.current.querySelectorAll('a[href], button:not([disabled])');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
        if (e.key === 'Escape') {
          setIsMobileMenuOpen(false);
        }
      };
      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();
      return () => {
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isMobileMenuOpen]);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setActiveDropdown(null);
    }
  };
  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };
  const services = [{
    name: 'Web Design',
    path: '/services/web-design'
  }, {
    name: 'E-Commerce',
    path: '/services/ecommerce'
  }, {
    name: 'Branding',
    path: '/services/branding'
  }, {
    name: 'SEO Services',
    path: '/services/seo'
  }, {
    name: 'Website Maintenance',
    path: '/services/maintenance'
  }, {
    name: 'Content Writing',
    path: '/services/content'
  }];
  const policies = [{
    name: 'All Policies',
    path: '/policies'
  }, {
    name: 'Privacy & Cookies',
    path: '/privacy'
  }, {
    name: 'Cancellation Policy',
    path: '/cancellation'
  }, {
    name: 'Terms & Conditions',
    path: '/terms'
  }];
  // Determine header background color based on page and scroll state
  const getHeaderBgClass = () => {
    return 'bg-white dark:bg-[#0C1B26] shadow-md';
  };
  // Determine text color for links based on page and scroll state
  const getLinkColorClass = (isActive: boolean) => {
    if (isActive) return 'text-[#0DC3DC]';
    return 'text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]';
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderBgClass()}`} data-id="element-658">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-659">
        <div className="flex items-center justify-between h-24 md:h-28" data-id="element-660">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-id="element-661">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studios - UK Web Design" className="h-20 md:h-28" data-id="element-662" />
          </Link>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8" data-id="element-663">
            <Link to="/" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/')}`} data-id="element-664">
              Home
            </Link>
            {/* Services Dropdown */}
            <div className="relative group" data-id="element-665">
              <button className={`flex items-center text-sm font-medium transition-colors text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]`} data-id="element-666">
                Services
                <ChevronDownIcon size={16} className="ml-1" data-id="element-667" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1E1E1E] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-668">
                {services.map(service => <Link key={service.path} to={service.path} className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 first:rounded-t-lg last:rounded-b-lg" data-id="element-669">
                    {service.name}
                  </Link>)}
              </div>
            </div>
            <Link to="/pricing" className={`text-sm font-semibold transition-all px-4 py-2 rounded-full ${location.pathname === '/pricing' ? 'bg-[#0DC3DC] text-white shadow-md' : 'bg-[#0DC3DC]/10 text-[#0DC3DC] hover:bg-[#0DC3DC]/20'}`} data-id="element-670">
              Pricing
            </Link>
            <Link to="/portfolio" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/portfolio')}`} data-id="element-671">
              Portfolio
            </Link>
            <Link to="/about" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/about')}`} data-id="element-672">
              About Us
            </Link>
            <Link to="/contact" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/contact')}`} data-id="element-673">
              Contact Us
            </Link>
            <Link to="/faq" className={`text-sm font-medium transition-colors ${getLinkColorClass(location.pathname === '/faq')}`} data-id="element-674">
              FAQ
            </Link>
            {/* Policies Dropdown */}
            <div className="relative group" data-id="element-675">
              <button className={`flex items-center text-sm font-medium transition-colors text-gray-700 dark:text-gray-200 hover:text-[#0DC3DC]`} data-id="element-676">
                Policies
                <ChevronDownIcon size={16} className="ml-1" data-id="element-677" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1E1E1E] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-678">
                {policies.map(policy => <Link key={policy.path} to={policy.path} className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 first:rounded-t-lg last:rounded-b-lg" data-id="element-679">
                    {policy.name}
                  </Link>)}
              </div>
            </div>
            <Link to="/contact" className="px-6 py-2 bg-[#0DC3DC] text-white font-medium rounded-lg hover:bg-[#0DC3DC]/90 transition-colors" data-id="element-680">
              Get Started
            </Link>
          </div>
          {/* Mobile Menu Button */}
          <button onClick={toggleMobileMenu} className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-681">
            {isMobileMenuOpen ? <XIcon size={24} className="text-gray-700 dark:text-white" data-id="element-682" /> : <MenuIcon size={24} className="text-gray-700 dark:text-white" data-id="element-683" />}
          </button>
        </div>
        {/* Mobile Menu */}
        <AnimatePresence data-id="element-684">
          {isMobileMenuOpen && <motion.div ref={mobileMenuRef} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="lg:hidden overflow-hidden bg-[#0C1B26] border-t border-gray-800" role="dialog" aria-modal="true" aria-label="Mobile navigation menu" data-id="element-685">
              <div className="py-4 space-y-2" data-id="element-686">
                <Link to="/" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-687">
                  Home
                </Link>
                {/* Mobile Services Dropdown */}
                <div data-id="element-688">
                  <button onClick={() => toggleDropdown('services')} className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-200 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-689">
                    Services
                    <ChevronDownIcon size={20} className={`transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} data-id="element-690" />
                  </button>
                  <AnimatePresence data-id="element-691">
                    {activeDropdown === 'services' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="pl-4 space-y-1 overflow-hidden" data-id="element-692">
                        {services.map(service => <Link key={service.path} to={service.path} className="block px-4 py-2 text-sm text-gray-300 hover:text-[#0DC3DC] transition-colors" data-id="element-693">
                            {service.name}
                          </Link>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/pricing" className={`block mx-4 px-4 py-3 text-base font-semibold rounded-lg transition-colors ${location.pathname === '/pricing' ? 'bg-[#0DC3DC] text-white shadow-md' : 'bg-[#0DC3DC]/20 text-[#0DC3DC] border border-[#0DC3DC]/30'}`} data-id="element-694">
                  Pricing
                </Link>
                <Link to="/portfolio" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/portfolio' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-695">
                  Portfolio
                </Link>
                <Link to="/about" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/about' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-696">
                  About Us
                </Link>
                <Link to="/contact" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/contact' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-697">
                  Contact Us
                </Link>
                <Link to="/faq" className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${location.pathname === '/faq' ? 'bg-[#0DC3DC]/10 text-[#0DC3DC]' : 'text-gray-200 hover:bg-gray-800'}`} data-id="element-698">
                  FAQ
                </Link>
                {/* Mobile Policies Dropdown */}
                <div data-id="element-699">
                  <button onClick={() => toggleDropdown('policies')} className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-200 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-700">
                    Policies
                    <ChevronDownIcon size={20} className={`transition-transform ${activeDropdown === 'policies' ? 'rotate-180' : ''}`} data-id="element-701" />
                  </button>
                  <AnimatePresence data-id="element-702">
                    {activeDropdown === 'policies' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className="pl-4 space-y-1 overflow-hidden" data-id="element-703">
                        {policies.map(policy => <Link key={policy.path} to={policy.path} className="block px-4 py-2 text-sm text-gray-300 hover:text-[#0DC3DC] transition-colors" data-id="element-704">
                            {policy.name}
                          </Link>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/contact" className="block mx-4 mt-4 px-6 py-3 bg-[#0DC3DC] text-white font-medium text-center rounded-lg hover:bg-[#0DC3DC]/90 transition-colors" data-id="element-705">
                  Get Started
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
}